import React from "react";
import ReactDOM from 'react-dom/client';
import './index.css';
import reportWebVitals from './reportWebVitals';
import {
  createBrowserRouter,
  RouterProvider,
} from "react-router-dom";
import ListingSearchPage from './listingSearchPage';
import { SignupPage } from './auth/signup';
import { LoginPage } from './auth/login';
import { ForgotPasswordPage } from './auth/forgotPassword';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import WithProviders from "./WithProviders";
import MessagesPage from "./messagesPage/messagesPage";
import OrderConfirmPage from "./checkout/confirm/orderConfirm";
import LandingPage from "./landingPage";
import OrderTransactionFailedPage from "./checkout/orderTransactionFailedPage";
import OrderPendingPage from "./checkout/orderPending/orderPendingPage";
import OrderTransactionCancledPage from "./checkout/orderTransactionCancledPage";
import OrderHistoryPage from "./orderHistoryPage/OrderHistoryPage";
import OrderManagementPage from "./orderManagement/orderManagementPage";
import RefundRequestPage from "./refund/requestRefund/requestRefundPage";
import RefundManagementPage from "./refund/refundRequestManagement/refundManagementPage";
import RefundOrderInStore from "./refund/refundOrderInStore/refundOrderInStore";
import FullScreenMap from "./profilePage/map";
import { ProfileManagementPage } from "./profilePage/manageProfile/profileManagementPage";
import ListingDetailsPage from "./listingDetailsPage";
import FullMenuDetailsPage from "./listingDetailsPage/fullMenuDetails"
import OrderSuccessPage from "./checkout/orderSuccessful/orderSuccessful";
import { ToastContainer, toast } from 'react-toastify';
import { StaticContentPage } from "./static/staticContentPage";
import FAQPage from "./static/FAQ";
import { MetricsNames } from "./services/firebase/metricsService";
import { Metric } from "web-vitals";
import { VerifyEmailPage } from "./auth/verifyEmail";
import DashboardPage from "./dashboard/Dashboard";
import { logEvent } from "./firebase";
import { register } from './serviceWorkerRegistration';
import { ViewCustomerRefundPage } from "./refund/viewRefund/viewRefundPage";
import { AdminPage } from "./adminPortal/AdminPortal";
import { OrderCollectPage } from "./checkout/collect/orderCollectPage";
import UpsertFullMenuPage from "./profilePage/maintainSpecificRestaurant/upsertFullMenu";


const router = createBrowserRouter([
  {
    path: "/",
    element: <WithProviders><LandingPage /></WithProviders>,
  },
  {
    path: "/login",
    element: <WithProviders><LoginPage /></WithProviders>,
  },
  {
    path: "/signup",
    element: <WithProviders><SignupPage /></WithProviders>,
  },
  {
    path: "/verify-success",
    element: <WithProviders><VerifyEmailPage /></WithProviders>,
  },
  {
    path: "/terms/:pageType",
    element: <WithProviders><StaticContentPage optionalPageType={null} /></WithProviders>,
  },
  {
    path: "/about",
    element: <WithProviders><StaticContentPage optionalPageType={"about"} /></WithProviders>,
  },
  {
    path: "/faq",
    element: <WithProviders><FAQPage /></WithProviders>,
  },
  {
    path: "/forgotPassword",
    element: <WithProviders><ForgotPasswordPage /></WithProviders>,
  },
  {
    path: "/profile",
    element: <WithProviders><ProfileManagementPage /></WithProviders>,
  },
  {
    path: "/profile/map/:address/:lat/:lng",
    element: <WithProviders><FullScreenMap /></WithProviders>
  },
  {
    path: "/maintainRestaurant/refundOrderInStore/:orderId",
    element: <WithProviders actionTypes="dashboard" enableHeaderHomeNavigation={false}><RefundOrderInStore /></WithProviders>,
  },
  {
    path: "/search",
    element: <WithProviders><ListingSearchPage /></WithProviders>,
  },
  {
    path: "/listing/:id",
    element: <WithProviders><ListingDetailsPage /></WithProviders>,
  },
  {
    path: "/messages",
    element: <WithProviders><MessagesPage /></WithProviders>,
  },
  {
    path: "/order/confirm",
    element: <WithProviders><OrderConfirmPage /></WithProviders>,
  },
  {
    path: "/order/pending",
    element: <WithProviders><OrderPendingPage /></WithProviders>,
  },
  {
    path: "/order/success",
    element: <WithProviders><OrderSuccessPage /></WithProviders>,
  },
  {
    path: "/order/collect/:orderId",
    element: <WithProviders><OrderCollectPage /></WithProviders>,
  },
  {
    path: "/order/cancled",
    element: <WithProviders><OrderTransactionCancledPage /></WithProviders>,
  },
  {
    path: "/order/failed",
    element: <WithProviders><OrderTransactionFailedPage /></WithProviders>,
  },
  {
    path: "/order/history",
    element: <WithProviders><OrderHistoryPage /></WithProviders>,
  },
  {
    path: "/order/history/refund/:orderId",
    element: <WithProviders><RefundRequestPage /></WithProviders>,
  },
  {
    path: "/order/history/viewRefund/:refundId",
    element: <WithProviders><ViewCustomerRefundPage /></WithProviders>,
  },
  {
    path: "/refund/management/:refundId",
    element: <WithProviders actionTypes="dashboard" enableHeaderHomeNavigation={false}><RefundManagementPage /></WithProviders>,
  },
  {
    path: "/order/management/:id",
    element: <WithProviders><OrderManagementPage /></WithProviders>
  },
  {
    path: "/dashboard/",
    element: <WithProviders showHeader={false}><DashboardPage /></WithProviders>
  },
  {
    path: "/adminPortal/",
    element: <WithProviders showHeader={false}><AdminPage /></WithProviders>
  },
  {
    path: "/dashboard/:restaurantId/editMenu/",
    element: <WithProviders actionTypes="dashboard" enableHeaderHomeNavigation={false}><UpsertFullMenuPage /></WithProviders>
  },
]);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const theme = createTheme();


logEvent(MetricsNames.LOADED_PAGE)


root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <RouterProvider router={router} />
      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar={false}
        closeOnClick={true}
        pauseOnHover={true}
        draggable={true}
        limit={1}
        className="custom-toast-container"
      />
    </ThemeProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// Typed handler for Web Vitals metrics
function sendToAnalytics(metric: Metric): void {
  const { id, name, value } = metric;

  // Only log if analytics is available
  logEvent(name, {
    eventCategory: "Web Vitals",
    eventAction: name,
    eventValue: Math.round(name === "CLS" ? value * 1000 : value), // must be integer
    eventLabel: id,    // unique ID for this page load
    nonInteraction: true, // doesn’t affect bounce rate
  })

}

reportWebVitals(sendToAnalytics);
register();
